// Size-choice guard (C9, 2026-10-15). Horizon preselects the first size; until the shopper taps
// a size themselves, in every purchase scope (the product page's .product-information and each
// quick-add modal):
// - the size fieldset carries data-pb-size-unchosen (pb-system.css drops the preselected look);
// - add to cart scrolls to the picker with an inline prompt instead of adding;
// - Shop Pay / more payment options are made inert and covered by a shield that does the same.
// On the product page a ?variant= link at page load counts as a chosen size; quick-add modals
// start unchosen every time they open. Only scopes with a size option of 2+ values are guarded.
// Copy comes from #pb-size-choice-copy (layout/theme.liquid).
// <pb-mobile-size-cta> (snippets/pb-pdp-mobile-size-cta.liquid) is the product page's mobile
// bottom bar: "Choose size" until a size is chosen, then "Add to cart · <size>".
(() => {
  if (window.pbSizeChoice) return;

  const SIZE_NAMES = ['size', 'größe', 'taille', 'tamaño', 'talla', 'dimensione', 'taglia', 'grootte', 'maat', 'rozmiar', 'storlek', 'koko', 'størrelse'];
  let copy = {};
  try { copy = JSON.parse(document.getElementById('pb-size-choice-copy')?.textContent || '{}'); } catch (error) { copy = {}; }
  const chooseText = (copy.choose || 'Select size').trim();
  const addText = (copy.add || 'Add to cart').trim();

  // Horizon re-renders the product form on every variant change, so the product page's choice is
  // kept here rather than on any element.
  let pageChosen = new URLSearchParams(window.location.search).has('variant');
  const sizeText = (value) => value.replace(/\s*-?\s*cm$/i, '');

  class Guard {
    constructor(scope, modal) {
      this.scope = scope;
      this.modal = modal;
      this.modalChosen = false;
      this.frame = 0;
      this.schedule = () => { if (!this.frame) this.frame = requestAnimationFrame(() => { this.frame = 0; this.update(); }); };
      // Only a shopper's own tap counts as choosing a size (not Horizon's preselection).
      scope.addEventListener('change', (event) => {
        if (event.isTrusted && this.target?.contains(event.target)) { this.chosen = true; this.update(); }
      });
      if (modal) scope.addEventListener('close', () => { this.modalChosen = false; this.update(); });
      // Picker markup is swapped after variant changes / quick-add loads, and Shopify renders the
      // payment button late: re-apply the unchosen state once new markup is in.
      new MutationObserver(() => { if (!this.chosen) this.schedule(); }).observe(scope, {childList: true, subtree: true});
      this.update();
    }

    get chosen() { return this.modal ? this.modalChosen : pageChosen; }
    set chosen(value) { if (this.modal) this.modalChosen = value; else pageChosen = value; }

    get target() {
      return Array.from(this.scope.querySelectorAll('fieldset.variant-option')).find((fieldset) => {
        const legend = (fieldset.querySelector('legend')?.textContent || '').trim().toLowerCase();
        return SIZE_NAMES.some((name) => legend.startsWith(name)) && fieldset.querySelectorAll('input[type="radio"]').length > 1;
      });
    }

    get atc() { return this.scope.querySelector('[ref="addToCartButton"], button[name="add"]'); }

    get active() { return !!this.target; }

    update() {
      const picker = this.target;
      const guarded = !!picker && !this.chosen;
      picker?.toggleAttribute('data-pb-size-unchosen', guarded);
      if (!guarded) this.scope.querySelector('.pb-size-required')?.remove();
      this.syncExpressCheckout(guarded);
    }

    syncExpressCheckout(guarded) {
      const pay = this.scope.querySelector('.shopify-payment-button');
      if (!pay) return;
      for (const child of pay.children) {
        if (!child.classList.contains('pb-size-shield')) child.toggleAttribute('inert', guarded);
      }
      let shield = pay.querySelector(':scope > .pb-size-shield');
      if (!guarded) { shield?.remove(); return; }
      if (shield) return;
      shield = document.createElement('button');
      shield.type = 'button';
      shield.className = 'pb-size-shield';
      shield.setAttribute('aria-label', chooseText);
      shield.addEventListener('click', () => this.promptSize());
      pay.append(shield);
    }

    promptSize() {
      const picker = this.target;
      if (!picker) return;
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      picker.scrollIntoView({block: 'center', behavior: reduce ? 'auto' : 'smooth'});
      if (!picker.querySelector('.pb-size-required')) {
        const note = document.createElement('p');
        note.className = 'pb-size-required';
        note.setAttribute('role', 'alert');
        note.textContent = chooseText;
        picker.querySelector('legend')?.after(note);
      }
      const input = picker.querySelector('input:checked') || picker.querySelector('input');
      input?.focus({preventScroll: true});
    }
  }

  const guards = new WeakMap();
  const guardFor = (scope, modal) => {
    if (!guards.has(scope)) guards.set(scope, new Guard(scope, modal));
    return guards.get(scope);
  };
  const scopeOf = (node) => {
    const modal = node.closest?.('dialog.quick-add-modal');
    if (modal) return [modal, true];
    const page = node.closest?.('.product-information');
    return page ? [page, false] : [null, false];
  };
  const attachAll = () => {
    document.querySelectorAll('.product-information').forEach((scope) => guardFor(scope, false));
    document.querySelectorAll('dialog.quick-add-modal').forEach((scope) => guardFor(scope, true));
  };

  // Window capture phase: runs before Horizon's document-level on:click delegation, so the
  // add-to-cart animation never plays for a size nobody chose.
  window.addEventListener('click', (event) => {
    const [scope, modal] = scopeOf(event.target);
    if (!scope) return;
    const guard = guardFor(scope, modal);
    const atc = guard.atc;
    if (guard.chosen || !guard.active || !atc || atc.disabled || !atc.contains(event.target)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    guard.promptSize();
  }, true);

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', attachAll);
  else attachAll();
  // Quick-add modals may be created late: attach on open.
  document.addEventListener('toggle', attachAll, true);
  document.addEventListener('dialog:open', attachAll, true);
  window.addEventListener('click', () => setTimeout(attachAll, 0), true);

  // Cart-drawer upsell (third-party app, React-rendered): its variant <select> preselects the first
  // size. Leave its DOM alone (React owns it); only add attributes, CSS shows the placeholder, and
  // block its Add button until the shopper picks. The select is left with no selected option
  // (selectedIndex -1) while unchosen, so picking any size, the first one included, fires change.
  const upsellSelector = '.wizz-upsell-productInfor-content-selectBox-select-custom select';
  const upsellChosen = new Set();
  const upsellCard = (node) => node.closest?.('.wizz-upsell-productInfor-content-custom');
  const upsellKey = (card) => card?.querySelector('a[href]')?.getAttribute('href') || '';
  const isSizeSelect = (select) => new Set(Array.from(select.options)
    .map((option) => (option.text.match(/^\s*(\d{2,3})\s*-?\s*cm\b/i) || [])[1])
    .filter(Boolean)).size > 1;
  // The upsell select is narrow, so its placeholder is the product's own (localized) size option
  // name, e.g. "Size" / "Größe"; chooseText until that has loaded.
  const upsellLabels = new Map();
  const loadUpsellLabel = (key) => {
    if (upsellLabels.has(key)) return;
    upsellLabels.set(key, null);
    const handle = (key.match(/\/products\/([^/?#]+)/) || [])[1];
    if (!handle) return;
    const root = window.Shopify?.routes?.root || '/';
    fetch(`${root}products/${handle}.js`)
      .then((response) => (response.ok ? response.json() : null))
      .then((product) => {
        const name = product?.options?.map((option) => option.name || option)
          .find((option) => SIZE_NAMES.includes(String(option).trim().toLowerCase()));
        if (name) { upsellLabels.set(key, String(name).trim()); syncUpsells(); }
      })
      .catch(() => {});
  };
  const syncUpsells = () => {
    document.querySelectorAll(upsellSelector).forEach((select) => {
      const card = upsellCard(select);
      const key = upsellKey(card);
      const guarded = !!card && isSizeSelect(select) && !upsellChosen.has(key);
      select.toggleAttribute('data-pb-size-unchosen', guarded);
      if (!guarded) { select.parentElement.removeAttribute('data-pb-size-placeholder'); return; }
      if (select.selectedIndex !== -1) select.selectedIndex = -1;
      loadUpsellLabel(key);
      const label = upsellLabels.get(key) || chooseText;
      if (select.parentElement.getAttribute('data-pb-size-placeholder') !== label) select.parentElement.setAttribute('data-pb-size-placeholder', label);
      if (select.getAttribute('aria-label') !== chooseText) select.setAttribute('aria-label', chooseText);
    });
  };
  let upsellFrame = 0;
  new MutationObserver(() => {
    if (!upsellFrame) upsellFrame = requestAnimationFrame(() => { upsellFrame = 0; syncUpsells(); });
  }).observe(document.body, {childList: true, subtree: true});
  const chooseUpsell = (event) => {
    if (!event.isTrusted || !event.target.matches?.(upsellSelector)) return;
    upsellChosen.add(upsellKey(upsellCard(event.target)));
    syncUpsells();
  };
  window.addEventListener('change', chooseUpsell, true);
  window.addEventListener('click', (event) => {
    const add = event.target.closest?.('.w4-storefront-upsell-btnAdd');
    const select = add && upsellCard(add)?.querySelector(upsellSelector);
    if (!select?.hasAttribute('data-pb-size-unchosen')) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    select.selectedIndex = -1;
    select.focus();
    try { select.showPicker(); } catch (error) { /* focus is the fallback */ }
  }, true);

  window.pbSizeChoice = {guardFor};

  if (customElements.get('pb-mobile-size-cta')) return;
  customElements.define('pb-mobile-size-cta', class extends HTMLElement {
    connectedCallback() {
      const scope = this.closest('.product-information');
      if (!scope) return;
      this.guard = guardFor(scope, false);
      if (!this.guard.active) return;
      this.mobile = window.matchMedia('(max-width: 749px)');
      this.label = this.querySelector('.pb-mobile-size-cta__label');
      this.frame = 0;
      const offscreen = (el) => { const r = el.getBoundingClientRect(); return r.top > window.innerHeight - 24 || r.bottom < 0; };
      this.update = () => {
        this.frame = 0;
        const {chosen, atc, target: picker} = this.guard;
        const target = chosen && atc ? atc : picker;
        if (!target) return;
        const show = this.mobile.matches && offscreen(target);
        this.hidden = !show;
        document.body.classList.toggle('pb-size-cta-on', show);
        const text = !chosen || !atc ? chooseText
          : atc.disabled ? atc.textContent.trim()
          : `${addText} · ${sizeText(picker?.querySelector('input:checked')?.value || '')}`;
        if (this.label.textContent !== text) this.label.textContent = text;
        this.querySelector('button').disabled = !!(chosen && atc?.disabled);
      };
      this.schedule = () => { if (!this.frame) this.frame = requestAnimationFrame(this.update); };
      this.querySelector('button').addEventListener('click', () => {
        const {chosen, atc} = this.guard;
        if (chosen && atc && !atc.disabled) { atc.click(); return; }
        this.guard.promptSize();
      });
      scope.addEventListener('change', this.schedule);
      window.addEventListener('scroll', this.schedule, {passive: true});
      window.addEventListener('resize', this.schedule);
      this.mobile.addEventListener('change', this.schedule);
      this.update();
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.schedule);
      window.removeEventListener('resize', this.schedule);
      this.mobile?.removeEventListener('change', this.schedule);
      document.body.classList.remove('pb-size-cta-on');
    }
  });
})();
